<script lang="ts">
	import type { Article } from "#lib/types/article.js";
	import type { InternalLinkingModule, Question } from "#lib/types/shared.js";
	import MetaData from "#lib/MetaData.svelte";
	import ArticleHeader from "#lib/ArticleHeader.svelte";
	import ArticleSummary from "#lib/ArticleSummary.svelte";
	import ArticlePage from "#lib/PostPage.svelte";
	import type { PageData } from "./$types";
	import Faq from "#lib/FAQ.svelte";
	import InternalLinking from "#lib/InternalLinking.svelte";
	import getTextFromPortableText from "#lib/utils/getTextFromPortableText.js";
	import { useQuery } from "@sanity/sveltekit";

	type ArticleSlugPage = Article & {
		faq?: {
			heading: string;
			items: Question[];
		};
	};

	let { data }: { data: PageData } = $props();

	// Server-filtered article (always available)
	let serverArticle: ArticleSlugPage = $derived(data?.sanity?.options?.initial?.data);

	// Live-updating query for Sanity visual editing preview. Gated on
	// previewEnabled because `useQuery` captures params at init and won't
	// react to client-side navigation — outside preview it would shadow
	// `serverArticle` with stale data.
	const q = useQuery(data?.sanity?.query, data?.sanity?.params, {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		initial: data?.sanity?.queryInitial as any,
	});
	let liveArticle = $derived(data?.previewEnabled && Array.isArray($q?.data) ? $q.data[0] : null);

	// Use live data when available (preview), otherwise server data
	let article: ArticleSlugPage = $derived(liveArticle ?? serverArticle);
	let internalLinkingModule: InternalLinkingModule = $derived(data?.internalLinkingModule);
</script>

<MetaData
	data={{
		title: article?.seo?.title || article?.title,
		description: article?.seo?.description || article?.lead,
		image: data?.image,
		imageAlt: data?.imageAlt,
		imageWidth: data?.imageWidth,
		imageHeight: data?.imageHeight,
		type: "article",
		datePublished: article?.publishedAt,
		lastReviewed: article?.seo?.lastReviewed,
		noIndex: article?.seo?.noIndex,
		noFollow: article?.seo?.noFollow,
		author: article?.author,
	}}
	jsonLd={true}
	breadcrumbs={data?.breadcrumbs}
	faq={article?.faq?.items?.map((item) => ({
		question: item.question,
		answer: getTextFromPortableText(item.answer),
	}))}
/>

{#if article?.content}
	<ArticlePage content={article.content} center>
		<!-- {#if data?.heroImage} -->
		<ArticleHeader
			title={article?.title}
			lead={article?.lead}
			author={article?.author}
			publishedAt={article?.publishedAt}
			lastReviewed={article?.seo?.lastReviewed}
			image={data?.heroImage ? data.heroImage : undefined}
		/>
		<!-- {/if} -->

		<ArticleSummary summary={article?.summary} />

		{#snippet below()}
			<section class="max-w-prose mx-a">
				{#if article?.faq}
					<h2>{article.faq.heading || `Spørsmål og svar`}</h2>
					<div class="border-solid border-3 rounded-2xl p-2">
						{#each article.faq.items as question (question)}
							<Faq answer={question} />
						{/each}
					</div>
				{/if}
			</section>
		{/snippet}
	</ArticlePage>
{/if}

{#if internalLinkingModule}
	<InternalLinking module={internalLinkingModule} />
{/if}
